import { Id } from 'types/definitions'
import DocumentStore, {
  type UploadDocumentRequest,
} from 'documents/DocumentStore'
import Document from 'documents/Document'
import {
  assertValidFileType,
  assertValidFileSize,
  validateFileExtension,
} from 'utils/file-utils'
import { extractMessageFromError } from 'utils/error-util'

export interface SuccessfulUploadDocRes {
  success: true
  file: File
  doc: Document
}

interface ErrorUploadDocRes {
  success: false
  file: File
  error: unknown
}

export type UploadDocResult = SuccessfulUploadDocRes | ErrorUploadDocRes

export class DocumentActionsAdapter {
  constructor(private documentStore: DocumentStore) {}

  downloadDocument(document: Document, inline = false) {
    return this.documentStore.download(document, inline)
  }

  downloadAll(documentIds: Id[]) {
    return this.documentStore.downloadAll(documentIds)
  }

  archiveDocument(document: Document) {
    return this.documentStore.archiveDocument(Id.assert(document.id))
  }

  async uploadNewDocument(
    request: UploadDocumentRequest,
  ): Promise<UploadDocResult> {
    const { file } = request
    try {
      // Validate file has an extension
      validateFileExtension(file)
      assertValidFileType(file)
      assertValidFileSize(file)

      const doc = await this.documentStore.uploadDocument(request)
      return {
        success: true,
        file,
        doc,
      }
    } catch (err) {
      return {
        success: false,
        file,
        error: extractMessageFromError(err),
      }
    }
  }

  async confirmDocument(documentLongId: Id, jobId: Id) {
    return this.documentStore.confirmDocument(documentLongId, jobId)
  }

  async unlinkDocument(document: Document, jobId: Id) {
    return this.documentStore.removeLink(document, Id.assert(jobId))
  }
}
